import { useState } from 'react';

// Hardcoded data for reported emails
const reportedEmails = [
  { 
    id: 1, 
    subject: 'Urgent: Update Your Account Information', 
    sender: 'security@hankcorporation.com',
    reportedAt: '2023-10-15 14:22:31', 
    status: 'confirmed',
    feedback: 'This was a simulated phishing email sent by your security team. Good catch!'
  },
  { 
    id: 2, 
    sender: 'noreply@google-docs.net',
    subject: 'Document shared with you: Q3 Financial Report', 
    reportedAt: '2023-10-12 09:46:15', 
    status: 'confirmed',
    feedback: 'Confirmed phishing attempt. The domain was spoofed to look like Google.'
  },
  { 
    id: 3, 
    sender: 'helpdesk@company.com',
    subject: 'Your password will expire today', 
    reportedAt: '2023-10-08 16:33:02', 
    status: 'legitimate',
    feedback: 'This was a legitimate email from our IT department.'
  },
];

const PhishingReporter = () => {
  const [formData, setFormData] = useState({
    sender: '',
    subject: '',
    receivedDate: '',
    description: '',
    attachForwarded: false,
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitSuccess(true);
      
      // Reset form
      setFormData({
        sender: '',
        subject: '',
        receivedDate: '',
        description: '',
        attachForwarded: false,
      });
      
      // Reset success message after a delay
      setTimeout(() => {
        setSubmitSuccess(false);
      }, 5000);
    }, 1500);
  };
  
  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">
        Phishing Reporter
      </h1>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Report Form */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Report a Suspicious Email</h2>
          
          {submitSuccess && (
            <div className="mb-4 bg-green-50 border-l-4 border-green-500 p-4">
              <p className="text-green-700">
                Thank you for your report! Our security team will investigate.
              </p>
            </div>
          )}
          
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <label htmlFor="sender" className="block text-sm font-medium text-gray-700 mb-1">
                  Sender Email
                </label>
                <input
                  type="text"
                  id="sender"
                  name="sender"
                  value={formData.sender}
                  onChange={handleChange}
                  className="input"
                  required
                  placeholder="e.g. suspicious@example.com"
                />
              </div>
              
              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                  Email Subject
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  className="input"
                  required
                  placeholder="e.g. Urgent: Action Required"
                />
              </div>
              
              <div>
                <label htmlFor="receivedDate" className="block text-sm font-medium text-gray-700 mb-1">
                  Date Received
                </label>
                <input
                  type="date"
                  id="receivedDate"
                  name="receivedDate"
                  value={formData.receivedDate}
                  onChange={handleChange}
                  className="input"
                  required
                />
              </div>
              
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                  Why is this email suspicious?
                </label>
                <textarea
                  id="description"
                  name="description"
                  rows="4"
                  value={formData.description}
                  onChange={handleChange}
                  className="input"
                  required
                  placeholder="Describe what made you suspicious about this email..."
                ></textarea>
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="attachForwarded"
                  name="attachForwarded"
                  checked={formData.attachForwarded}
                  onChange={handleChange}
                  className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                />
                <label htmlFor="attachForwarded" className="ml-2 block text-sm text-gray-700">
                  I've also forwarded the full email to security@company.com
                </label>
              </div>
              
              <div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full btn btn-primary"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Report'}
                </button>
              </div>
            </div>
          </form>
        </div>
        
        {/* Phishing Guidance */}
        <div className="card bg-blue-50">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">How to Identify Phishing</h2>
          
          <div className="space-y-4">
            <div>
              <h3 className="font-medium text-gray-900">Check the sender's email address</h3>
              <p className="text-sm text-gray-600">Verify that the email is from a legitimate domain and watch for slight misspellings.</p>
            </div>
            
            <div>
              <h3 className="font-medium text-gray-900">Be wary of urgent or threatening language</h3>
              <p className="text-sm text-gray-600">Phishing emails often create a sense of urgency to prompt immediate action.</p>
            </div>
            
            <div>
              <h3 className="font-medium text-gray-900">Look for poor spelling and grammar</h3>
              <p className="text-sm text-gray-600">Legitimate organizations typically have professional communications.</p>
            </div>
            
            <div>
              <h3 className="font-medium text-gray-900">Hover over links before clicking</h3>
              <p className="text-sm text-gray-600">Verify the URL to ensure it's going to the expected website.</p>
            </div>
            
            <div>
              <h3 className="font-medium text-gray-900">Be suspicious of unexpected attachments</h3>
              <p className="text-sm text-gray-600">Don't open attachments unless you're expecting them.</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Reported Emails History */}
      <div className="card mt-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Your Reported Emails</h2>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Subject
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sender
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Reported At
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {reportedEmails.map((email) => (
                <tr key={email.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {email.subject}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {email.sender}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {email.reportedAt}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full
                      ${email.status === 'confirmed' 
                        ? 'bg-green-100 text-green-800' 
                        : email.status === 'legitimate' 
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                      {email.status === 'confirmed' 
                        ? 'Confirmed Phishing' 
                        : email.status === 'legitimate' 
                          ? 'Legitimate Email'
                          : 'Under Review'
                      }
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PhishingReporter; 